import { faUtensils } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";
import React from "react";

import ArticleHeader from "@/mk2/apps/blogs/components/ArticleHeader";
import CommentsButton from "@/mk2/apps/blogs/components/CommentsButton";
import { RecipeIngredients } from "@/mk2/apps/blogs/components/RecipeIngredients";
import {
    CommentsDesign,
    MAGAZINE_USER,
    RECIPES_LIST_PAGES_ENABLED,
} from "@/mk2/apps/blogs/constants";
import stylesAA from "@/mk2/apps/blogs/containers/AdvancedArticle/AdvancedArticle.mscss";
import { getArticleTitleAsString } from "@/mk2/apps/blogs/containers/Article/Article.helpers";
import stylesA from "@/mk2/apps/blogs/containers/Article/Article.mscss";
import { Props } from "@/mk2/apps/blogs/containers/Article/ArticlePage";
import { revertReplacedTagsOfArticleScripts } from "@/mk2/apps/blogs/containers/ArticleEdit/ArticleEdit.helpers";
import Comments from "@/mk2/apps/blogs/containers/Comments/Comments";
import {
    RECIPES_SLUG,
    advancedArticleUrl,
    blogsAdvancedArticlePreviewGridUrl,
    blogsUrl,
} from "@/mk2/apps/blogs/urls";
import { getAdvnacedArticleBreadcrumbs } from "@/mk2/apps/blogs/utils";
import ReviewBlogPostPreview from "@/mk2/apps/wiki/components/ReviewBlogPostPreview";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { DeletedPostAlert } from "@/mk2/components/DeletedPostAlert";
import { Dot } from "@/mk2/components/Dot";
import { Img } from "@/mk2/components/Img";
import { ImgResponsive } from "@/mk2/components/ImgResponsive";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { StarsRating } from "@/mk2/components/StarsRating";
import { Failure } from "@/mk2/components/errors/Failure";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import Complaint from "@/mk2/containers/Complaint/Complaint";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import Complaints from "@/mk2/containers/Complaints/Complaints";
import Like, { LikeType } from "@/mk2/containers/Like/Like";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import {
    FilterAdditionalData,
    astToHtml,
    defaultFilterAdditionalData,
} from "@/mk2/helpers/article_ast/filters";
import { cacheLast } from "@/mk2/helpers/cache";
import { DeletedByRole } from "@/mk2/helpers/deleteable";
import { url } from "@/mk2/helpers/urls";
import { AdvancedArticleCategory, BlogPostEntity, NutritionMetric } from "@/mk2/schemas";
import { interpolate } from "@/mk2/services/i18n";
import {
    ARTICLE_COMPLAINT_TITLE,
    ARTICLE_DELETED_BY_ADMIN,
    ARTICLE_DELETED_BY_AUTHOR,
    ARTICLE_DELETED_BY_MODERATOR,
    BACK_TO_BLOGS,
    DIFFICULTY_EASY,
    DIFFICULTY_HARD,
    DIFFICULTY_MEDIUM,
    PB_RECOMMENDED_ARTICLES,
    POST_REMOVE_COMPLAINTS,
    RECIPE_DIFFICULTY_LEVEL_TEXT,
    RECIPE_NUTRITION_CALORIES,
    RECIPE_NUTRITION_CARBS,
    RECIPE_NUTRITION_ENERGY,
    RECIPE_NUTRITION_FAT,
    RECIPE_NUTRITION_FIBER,
    RECIPE_NUTRITION_PROTEIN,
    RECIPE_NUTRITION_VALUES_TITLE,
    RECIPE_PREPARATION_AND_COOKING_TIME,
    RECIPE_SERVINGS_COUNT,
    RECIPE_SERVINGS_COUNT_number,
    USER_LIKE,
    USER_UNLIKE,
    counter_RATINGS,
    number_MINUTES,
} from "@/mk/autogenerated/translations/AdvancedArticle.0714dc517a867b2b153db46c2521a7e8";
import assetUrl from "@/mk/common/assetUrl";

const deletedByToText = (deletedByRole: DeletedByRole): string => {
    return {
        [DeletedByRole.DeletedByAuthor]: ARTICLE_DELETED_BY_AUTHOR,
        [DeletedByRole.DeletedByAdmin]: ARTICLE_DELETED_BY_ADMIN,
        [DeletedByRole.DeletedByModerator]: ARTICLE_DELETED_BY_MODERATOR,
    }[deletedByRole];
};

export const difficultyText = {
    1: DIFFICULTY_EASY,
    2: DIFFICULTY_MEDIUM,
    3: DIFFICULTY_HARD,
} as const;

export default class AdvancedArticle extends React.Component<Props> {
    private articleAstToHtmlCache = cacheLast(true);
    private advancedArticleOnelinerAstToHtmlCache = cacheLast(true);
    private messageFormTextarea: HTMLTextAreaElement;

    public componentDidMount() {
        const {
            routeParams: { username, articleSlug },
            onPageView,
        } = this.props;
        // Only call pageView if username is available (may not be on group pages initially)
        if (username) {
            onPageView(username, articleSlug);
        }
    }

    public componentDidUpdate(prevProps: Readonly<Props>) {
        const {
            routeParams: { username, articleSlug },
            onPageView,
            articlePost,
            onStoreEvent,
        } = this.props;
        const prevUsername = prevProps.routeParams.username;

        // Call pageView when slug changes, or when username becomes available (group pages)
        if (prevProps.routeParams.articleSlug !== articleSlug || (!prevUsername && username)) {
            if (username) {
                onPageView(username, articleSlug);
            }
        }

        // Track recipes_detail_view when article loads or changes
        const prevArticleId = prevProps.articlePost?.id;
        const currentArticleId = articlePost?.id;
        if (currentArticleId && prevArticleId !== currentArticleId) {
            if (articlePost.advancedArticle?.category?.slug === RECIPES_SLUG) {
                onStoreEvent("recipes_detail_view", {
                    recipe_id: articlePost.id,
                    recipe_slug: articlePost.slug,
                });
            }
        }
    }

    public render() {
        const { loadingState, articlePost } = this.props;

        return (
            <div className={stylesA.Article}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderSuccess={this.renderLoadSuccess}
                    onRenderFailure={this.renderFailure}
                    hasData={!!articlePost}
                />
            </div>
        );
    }

    private load = () => {
        const {
            routeParams: { username, articleSlug },
            query,
            onLoad,
        } = this.props;
        const accessKey = query.key;
        onLoad(username, articleSlug, true, query, accessKey);
    };

    private renderLoadSuccess = () => {
        const {
            articlePost,
            commentsCount,
            complaintsCount,
            isMobile,
            showAds,
            location,
            requestUser,
            requestUserPermissions,
            suggestedArticles,
            advancedArticleListItemDetails,
            bookmarked,
            routeNavigate,
        } = this.props;

        const advancedArticle = articlePost?.advancedArticle;
        const mainCategoryId = advancedArticle?.category.id;

        const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;

        const breadcrumbInputs = getAdvnacedArticleBreadcrumbs(
            advancedArticle.subCategory || advancedArticle.category,
        );

        // prepare article body chunks
        let bodyChunks: React.ReactChild[] = null;
        if (articlePost.contentAST) {
            const additionalData: FilterAdditionalData = {
                ...defaultFilterAdditionalData,
                sourceApp: "blogs",
                sourceLocationPath: location.pathname,
                photos: articlePost.photos,
                customStyles: {
                    header2: cx(
                        stylesAA.AdvancedArticle__content__header2,
                        stylesAA.AdvancedArticle__content__opensans,
                    ),
                    header3: cx(
                        stylesA.Article__content__header3,
                        stylesAA.AdvancedArticle__content__opensans,
                    ),
                    header4: cx(
                        stylesA.Article__content__header4,
                        stylesAA.AdvancedArticle__content__opensans,
                    ),
                    paragraph: cx(
                        stylesA.Article__content__paragraph,
                        stylesAA.AdvancedArticle__content__opensans,
                        stylesAA.AdvancedArticle__content__paragraph,
                    ),
                    orderedlist: cx(
                        stylesA.Article__content__orderedlist,
                        stylesAA.AdvancedArticle__content__opensans,
                    ),
                    unorderedlist: cx(
                        stylesA.Article__content__unorderedlist,
                        stylesAA.AdvancedArticle__content__opensans,
                    ),
                    articleNote: cx(
                        stylesA.Article__content__articleNote,
                        stylesAA.AdvancedArticle__content__opensans,
                    ),
                    instagram: stylesA.Article__content__instagram,
                    tiktok: stylesA.Article__content__instagram,
                },
                countTopicContributors: articlePost.countTopicContributors,
                renderLinksWithNofollow: !(
                    articlePost.isPRArticle ||
                    articlePost.isCommercial ||
                    articlePost.user.username === MAGAZINE_USER
                ),
                linkedObjects: articlePost.contentASTAdditionalData.linkedObjects ?? {},
                productBoxes: articlePost.contentASTAdditionalData.productBoxes ?? {},
                extraEntities: {
                    wikiArticleArray: articlePost.extraEntitiesOfArticle,
                    strollerArray: articlePost.extraEntitiesOfStroller,
                    blogPostArray: articlePost.extraEntitiesOfPost,
                },
                hasResponsiveImages:
                    isMobile || requestUserPermissions.indexOf("wiki.can_edit_wiki") >= 0,

                // TODO - automatic suggestedItems are not used yet, will be defined
                // suggestedItems: suggestedArticles.map((blogPostArticle, i) => {
                //     return (
                //         <div key={i} className={stylesA.Article__suggestedArticle}>
                //             <div className={stylesA.Article__suggestedArticle__header}>{READ_ALSO}</div>
                //             <ReviewBlogPostPreview
                //                 photo={this.getArticlePhoto(blogPostArticle)}
                //                 title={getArticleTitleAsString(blogPostArticle.titleAST)}
                //                 postUrl={blogsArticleUrl(blogPostArticle)}
                //                 urlDataStoreClick={'suggested_article_clicked'}
                //                 badges={articleBadges[blogPostArticle.id]}
                //                 commentsCount={blogPostArticle.commentsCount}
                //                 likersCount={blogPostArticle.likeable.likesCount}
                //             />
                //         </div>
                //     );
                // }),
            };
            bodyChunks = this.articleAstToHtmlCache(
                () => astToHtml(articlePost.contentAST, additionalData),
                articlePost.contentAST,
            );
        }

        // prepare article body chunks
        let oneliner: React.ReactChild[] = null;
        if (advancedArticle.onelinerAST) {
            const additionalData: FilterAdditionalData = {
                ...defaultFilterAdditionalData,
                sourceApp: advancedArticle.category.slug,
                sourceLocationPath: location.pathname,
            };
            oneliner = this.advancedArticleOnelinerAstToHtmlCache(
                () => astToHtml(advancedArticle.onelinerAST, additionalData),
                advancedArticle.onelinerAST,
            );
        }

        // Time display: prep time, cook time, or both
        const prepTime = advancedArticle.preparationTimeMinutes;
        const cookTime = advancedArticle.cookTimeMinutes;
        const hasTime = !!(prepTime || cookTime);
        const hasBothTimes = !!(prepTime && cookTime);

        return (
            <div className={cx(stylesAA.AdvancedArticle__articlePage)}>
                <Complaint
                    id={articlePost.id}
                    entity={ComplaintTargetType.PhotoblogPost}
                    className={stylesA.Article__complaint}
                    scrollOffset={44 + 44 + 30 /* header + 30px spacing */}
                    inForum={false}
                    inGroups={false}
                    title={ARTICLE_COMPLAINT_TITLE}
                />

                {isAdmin && (
                    <Complaints
                        className={stylesA.Article__complaints}
                        id={articlePost.id}
                        entity={ComplaintTargetType.PhotoblogPost}
                        deleteLabel={POST_REMOVE_COMPLAINTS}
                        adminTheme={isAdmin}
                    />
                )}

                <div className={stylesAA.AdvancedArticle__sideMargins}>
                    <ArticleHeader
                        article={articlePost}
                        breadcrumbInputs={breadcrumbInputs}
                        hideBreadcrumbs={!RECIPES_LIST_PAGES_ENABLED}
                        isAdmin={
                            isAdmin ||
                            requestUserPermissions.indexOf("photoblog.can_manage_blogs") >= 0 ||
                            requestUserPermissions.indexOf("bo.can_manage_sales") >= 0 ||
                            requestUserPermissions.indexOf("bo.can_manage_finance") >= 0
                        }
                    />
                </div>

                {articlePost.deletedAt && (
                    <div className={stylesA.Article__announcements}>
                        {articlePost.deletedAt && (
                            <DeletedPostAlert
                                heading={deletedByToText(articlePost.deletedByRole)}
                                deletedByRole={articlePost.deletedByRole}
                                deletedBy={articlePost.deletedBy?.username}
                                deletedAt={articlePost.deletedAt}
                                reason={articlePost.deletedReason}
                            />
                        )}
                    </div>
                )}

                <div className={stylesAA.AdvancedArticle__sideMargins}>
                    {!!oneliner && (
                        <div className={stylesAA.AdvancedArticle__oneliner}>{oneliner}</div>
                    )}
                    {advancedArticle.coverPhoto && (
                        <div className={stylesAA.AdvancedArticle__coverPhoto}>
                            <ImgResponsive
                                alt=""
                                photo={advancedArticle.coverPhoto}
                                photoVersion="s1600x1600"
                            />
                        </div>
                    )}

                    <div className={stylesAA.AdvancedArticle__metaInfo}>
                        {hasTime && (
                            <div className={stylesAA.AdvancedArticle__metaInfo__item}>
                                <Img
                                    src={`${assetUrl}img/photoblog/recipes/timewatch@2x.png`}
                                    width={17}
                                    height={21}
                                    alt={RECIPE_PREPARATION_AND_COOKING_TIME}
                                />
                                <span>
                                    {hasBothTimes ? (
                                        <>
                                            {prepTime}
                                            {" + "}
                                            {interpolate(number_MINUTES, {
                                                count: cookTime,
                                                number: cookTime,
                                            })}
                                        </>
                                    ) : (
                                        interpolate(number_MINUTES, {
                                            count: cookTime || prepTime,
                                            number: cookTime || prepTime,
                                        })
                                    )}
                                </span>
                            </div>
                        )}
                        {advancedArticle.difficultyLevel && (
                            <div className={stylesAA.AdvancedArticle__metaInfo__item}>
                                <Img
                                    src={`${assetUrl}img/photoblog/recipes/signal-alt1@2x.png`}
                                    width={19}
                                    height={20}
                                    alt={RECIPE_DIFFICULTY_LEVEL_TEXT}
                                />
                                <span>{difficultyText[advancedArticle.difficultyLevel]}</span>
                            </div>
                        )}

                        {advancedArticle.category.id === AdvancedArticleCategory.RECIPES &&
                            (advancedArticle.servingsCount || advancedArticle.servingsNote) && (
                                <div className={stylesAA.AdvancedArticle__metaInfo__item}>
                                    <Img
                                        src={`${assetUrl}img/photoblog/recipes/soup@2x.png`}
                                        width={21}
                                        height={21}
                                        alt={RECIPE_SERVINGS_COUNT}
                                    />
                                    <span>
                                        {advancedArticle.servingsNote
                                            ? advancedArticle.servingsNote
                                            : interpolate(RECIPE_SERVINGS_COUNT_number, {
                                                  count: advancedArticle.servingsCount,
                                                  number: advancedArticle.servingsCount,
                                              })}
                                    </span>
                                </div>
                            )}
                    </div>

                    {!!articlePost.starsRatingCount && (
                        <div className={stylesAA.AdvancedArticle__starsRating}>
                            <StarsRating starsCount={articlePost.starsRatingScore} />
                            <b>
                                &nbsp;
                                {articlePost.starsRatingScore?.toLocaleString("sk-SK", {
                                    minimumFractionDigits: 1,
                                    maximumFractionDigits: 1,
                                })}
                            </b>
                            <Dot />
                            <Interpolate
                                i18nKey={counter_RATINGS}
                                counter={articlePost.starsRatingCount}
                                count={articlePost.starsRatingCount}
                            />
                        </div>
                    )}
                    {!!articlePost?.contentASTAdditionalData?.hashtags?.length && (
                        <div className={stylesAA.AdvancedArticle__hashtags}>
                            {articlePost.contentASTAdditionalData.hashtags.map((hashtagLabel) =>
                                RECIPES_LIST_PAGES_ENABLED ? (
                                    <Link
                                        key={hashtagLabel}
                                        to={url(blogsAdvancedArticlePreviewGridUrl, {
                                            advancedArticleCategorySlug:
                                                advancedArticle.category.slug,
                                            flag1: "h",
                                            value1: hashtagLabel,
                                        })}
                                    >
                                        #{hashtagLabel}
                                    </Link>
                                ) : (
                                    <span key={hashtagLabel}>#{hashtagLabel}</span>
                                ),
                            )}
                        </div>
                    )}
                    {RECIPES_LIST_PAGES_ENABLED &&
                        advancedArticle &&
                        (!bookmarked ? (
                            <div className={stylesAA.AdvancedArticle__bookmark}>
                                <Btn
                                    label={USER_LIKE}
                                    onClick={this.handleToggleBookmark}
                                    icon={faUtensils}
                                    type={BtnType.Link}
                                />
                            </div>
                        ) : (
                            <div className={stylesAA.AdvancedArticle__bookmark}>
                                <Btn
                                    label={USER_UNLIKE}
                                    onClick={this.handleToggleBookmark}
                                    icon={faUtensils}
                                    type={BtnType.Link}
                                />
                            </div>
                        ))}
                </div>

                {!!advancedArticleListItemDetails?.length && (
                    <div
                        className={cx(
                            stylesAA.AdvancedArticle__sideMargins,
                            stylesAA.AdvancedArticle__top50,
                        )}
                    >
                        <RecipeIngredients
                            advancedArticleListItemDetails={advancedArticleListItemDetails}
                            servingsCount={
                                mainCategoryId === AdvancedArticleCategory.RECIPES
                                    ? advancedArticle.servingsCount
                                    : null
                            }
                            servingsNote={
                                mainCategoryId === AdvancedArticleCategory.RECIPES
                                    ? advancedArticle.servingsNote
                                    : null
                            }
                        />
                    </div>
                )}

                {mainCategoryId === AdvancedArticleCategory.RECIPES &&
                    advancedArticle.nutritionValues?.length > 0 && (
                        <div
                            className={cx(
                                stylesAA.AdvancedArticle__sideMargins,
                                stylesAA.AdvancedArticle__nutritionSection,
                            )}
                        >
                            <div className={stylesAA.AdvancedArticle__nutritionTitle}>
                                {RECIPE_NUTRITION_VALUES_TITLE}
                            </div>
                            <div className={stylesAA.AdvancedArticle__nutritionGrid}>
                                {this.renderNutritionValue(
                                    advancedArticle,
                                    NutritionMetric.CALORIES,
                                    RECIPE_NUTRITION_CALORIES,
                                )}
                                {this.renderNutritionValue(
                                    advancedArticle,
                                    NutritionMetric.ENERGY,
                                    RECIPE_NUTRITION_ENERGY,
                                )}
                                {this.renderNutritionValue(
                                    advancedArticle,
                                    NutritionMetric.PROTEIN,
                                    RECIPE_NUTRITION_PROTEIN,
                                )}
                                {this.renderNutritionValue(
                                    advancedArticle,
                                    NutritionMetric.CARBS,
                                    RECIPE_NUTRITION_CARBS,
                                )}
                                {this.renderNutritionValue(
                                    advancedArticle,
                                    NutritionMetric.FAT,
                                    RECIPE_NUTRITION_FAT,
                                )}
                                {this.renderNutritionValue(
                                    advancedArticle,
                                    NutritionMetric.FIBER,
                                    RECIPE_NUTRITION_FIBER,
                                )}
                            </div>
                        </div>
                    )}

                {articlePost.contentAST && (
                    <div className={stylesAA.AdvancedArticle__content}>{bodyChunks}</div>
                )}

                {articlePost.scripts && this.renderDangerousDiv(articlePost.scripts)}

                {RECIPES_LIST_PAGES_ENABLED &&
                    articlePost.contentAST &&
                    suggestedArticles?.length > 0 && (
                        <div className={stylesAA.AdvancedArticle__sideMargins}>
                            <h2 className={stylesA.Article__suggestedArticles__header}>
                                {PB_RECOMMENDED_ARTICLES}
                            </h2>
                            {suggestedArticles.map((blogPostArticle, i) => (
                                <ReviewBlogPostPreview
                                    key={i}
                                    className={stylesA.Article__suggestedArticles__article}
                                    photo={this.getArticlePhoto(blogPostArticle)}
                                    title={getArticleTitleAsString(blogPostArticle.titleAST)}
                                    postUrl={advancedArticleUrl(blogPostArticle)}
                                    urlDataStoreClick={"suggested_article_clicked"}
                                    badges={null}
                                    commentsCount={blogPostArticle.commentsCount}
                                    likersCount={blogPostArticle.likeable.likesCount}
                                />
                            ))}
                            {/*<Btn*/}
                            {/*    type={BtnType.BlueOutline}*/}
                            {/*    label={'More recipes'}*/}
                            {/*    link={recipesHomepageUrl}*/}
                            {/*    className={stylesA.Article__suggestedArticles__button}*/}
                            {/*/>*/}
                        </div>
                    )}

                <div className={stylesA.Article__footer}>
                    <div className={stylesA.Article__footer__likes}>
                        <Like
                            entity="photoblog.post"
                            id={articlePost.id}
                            type={LikeType.IconCount}
                            className={stylesA.Article__footer__likes__like}
                            location={location}
                        />
                    </div>
                    <div className={stylesA.Article__footer__comments}>
                        <CommentsButton
                            commentsCount={commentsCount}
                            className={cx(
                                stylesA.Article__footer__comments__commentsButton,
                                complaintsCount &&
                                    stylesA[
                                        "Article__footer__comments__commentsButton--has-complaints"
                                    ],
                            )}
                            onClick={this.handleOnComments}
                        />
                    </div>
                </div>

                <Comments
                    key="article-comments"
                    parentPost={articlePost}
                    count={commentsCount}
                    design={CommentsDesign.Normal}
                    moderators={[]} // TBD
                    location={location}
                    routeNavigate={routeNavigate}
                    hasBlueToolbar
                    hasRating
                    requestUser={requestUser}
                    requestUserPermissions={requestUserPermissions}
                    isMobile={isMobile}
                    isPreloaded
                    onSetTextareaRef={this.handleSetMessageFormTextareaRef}
                />

                {showAds &&
                    (isMobile ? (
                        <AdSlot
                            zone="Blogs Article - Mid 01 - Mobile"
                            location={location}
                            marginTopWithAd={10}
                        />
                    ) : (
                        <div className={stylesA.Article__adSlotWrap}>
                            <AdSlot
                                zone="Blogs Article - Other 01 - Desktop"
                                location={location}
                                marginTopWithAd={30}
                            />
                        </div>
                    ))}
            </div>
        );
    };

    private renderFailure = () => {
        const { errorMessage } = this.props;
        return (
            <Failure
                errorMessage={errorMessage}
                onRetry={this.load}
                backButtonUrl={blogsUrl}
                backButtonLabel={BACK_TO_BLOGS}
            />
        );
    };

    private handleToggleBookmark = () => {
        const {
            onBookmark,
            articlePost,
            routeParams: { username },
            bookmarked,
            onStoreEvent,
        } = this.props;

        // Track bookmark click for recipes
        if (articlePost.advancedArticle?.category?.slug === RECIPES_SLUG) {
            onStoreEvent("recipes_bookmark_click", {
                recipe_id: articlePost.id,
                action: bookmarked ? "remove" : "add",
            });
        }

        onBookmark(
            articlePost.id,
            username, // Use article author's username from URL, not logged-in user
            articlePost.slug,
            !bookmarked,
        );
    };

    private handleOnComments = () => {
        if (this.messageFormTextarea) {
            this.messageFormTextarea.focus();
        }
    };

    private handleSetMessageFormTextareaRef = (ref: any) => {
        this.messageFormTextarea = ref;
    };

    private renderNutritionValue(advancedArticle: any, metric: NutritionMetric, label: string) {
        const nutritionValue = advancedArticle.nutritionValues?.find((nv) => nv.metric === metric);
        if (!nutritionValue || nutritionValue.amount === null) {
            return null;
        }
        return (
            <div className={stylesAA.AdvancedArticle__nutritionItem}>
                <span className={stylesAA.AdvancedArticle__nutritionItem__label}>{label}</span>
                <span className={stylesAA.AdvancedArticle__nutritionItem__value}>
                    {nutritionValue.amount}
                </span>
            </div>
        );
    }

    private renderDangerousDiv(scripts: string) {
        return (
            <div
                className={stylesA.Article__scripts}
                dangerouslySetInnerHTML={{ __html: revertReplacedTagsOfArticleScripts(scripts) }}
            />
        );
    }

    private getArticlePhoto = (blogPostArticle: BlogPostEntity) => {
        return (
            this.props.suggestedArticlesHomepagePhotos?.[blogPostArticle.id] ||
            blogPostArticle?.advancedArticle?.coverPhoto ||
            blogPostArticle?.photos?.[0]
        );
    };
}
